import { Close, Info } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { usePostApiProgramsByIdDeliverableTypesMutation } from '../../../../../../app/athenaApiGen';
import ProgramAvailableDeliverableTypesFilter from '../../../../../../components/filters/ProgramAvailableDeliverableTypesFilter';
import NumberField from '../../../../../../components/NumberField/NumberField';
import { AthenaSwitch } from '../../../../../../shared-theme/StyledComponents';
import { useProgramTypeContext } from '../useProgramTypesContext';

const AddTypeModal = () => {
  const {
    addTypeModal,
    setAddTypeModal,
    payload,
    setPayload,
    programDelType,
    setProgramDelType,
  } = useProgramTypeContext();
  const [error, setError] = useState<string | null>(null);
  const [addType, { isLoading }] =
    usePostApiProgramsByIdDeliverableTypesMutation();

  const isDeliverableNumberAvailable = useMemo(
    () =>
      !programDelType.find(
        (del) => del.deliverableNumber === payload.body[0]?.deliverableNumber,
      ),
    [payload.body[0]?.deliverableNumber],
  );

  useEffect(() => {
    setPayload((prev) => ({
      ...prev,
      body: [
        {
          deliverableNumber: 0,
          deliverableTypeId: '',
          source: '',
          requiresWorkflow: false,
          parentObjectType: 'Program',
        },
      ],
    }));
  }, []);

  const handleClose = () => {
    setAddTypeModal(false);
    setError('');
    setPayload((prev) => ({
      ...prev,
      body: [
        {
          deliverableNumber: 0,
          deliverableTypeId: '',
          source: '',
          requiresWorkflow: false,
          parentObjectType: 'Program',
        },
      ],
    }));
  };
  return (
    <Modal
      open={addTypeModal}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '650px',
          backgroundColor: 'var(--template-palette-background-paper)',
          border: '2px solid #000',
          boxShadow:
            '0px 12px 24px 0px rgba(0, 0, 0, 0.2), 0px 16px 48px 0px rgba(0, 0, 0, 0.15)',
          padding: '32px',
        }}
      >
        <Box
          sx={{
            marginBottom: 1,
            pb: 1,
            justifyContent: 'space-between',
            display: 'flex',
            flexDirection: 'row',
            alignSelf: 'center',
          }}
        >
          <Typography variant="h5">Add Deliverable Type</Typography>
          <IconButton size="small" onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
            <NumberField
              label="Deliverable #"
              required
              min={1}
              value={payload.body[0]?.deliverableNumber}
              onValueCommitted={(value) =>
                value &&
                setPayload((prev) => ({
                  ...prev,
                  body: [
                    {
                      ...payload.body[0],
                      deliverableNumber: value,
                    },
                  ],
                }))
              }
              size="small"
              errorText="Deliverable # is already in use or needs to be above 0."
              error={!isDeliverableNumberAvailable}
            />
            <ProgramAvailableDeliverableTypesFilter
              required
              programDeliverableTypeId={payload.body[0]?.deliverableTypeId}
              handleChange={(id) => {
                setPayload((prev) => ({
                  ...prev,
                  body: [
                    {
                      ...payload.body[0],
                      deliverableTypeId: (id as string) ?? '',
                    },
                  ],
                }));
              }}
            />
            <TextField
              label="Source"
              size="small"
              sx={{ flex: 1 }}
              required
              value={payload.body[0]?.source}
              onChange={(e) =>
                setPayload((prev) => ({
                  ...prev,
                  body: [
                    {
                      ...payload.body[0],
                      source: e.target.value,
                    },
                  ],
                }))
              }
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
            <TextField
              label="Gov Rights"
              size="small"
              sx={{ flex: 1 }}
              value={payload.body[0]?.govRights ?? ''}
              onChange={(e) =>
                setPayload((prev) => ({
                  ...prev,
                  body: [
                    {
                      ...payload.body[0],
                      govRights: e.target.value,
                    },
                  ],
                }))
              }
            />
            <TextField
              label="Document Refs"
              size="small"
              sx={{ flex: 1 }}
              value={payload.body[0]?.documentRef ?? ''}
              onChange={(e) =>
                setPayload((prev) => ({
                  ...prev,
                  body: [
                    {
                      ...payload.body[0],
                      documentRef: e.target.value,
                    },
                  ],
                }))
              }
            />
            <TextField
              label="When Due"
              size="small"
              sx={{ flex: 1 }}
              value={payload.body[0]?.whenDue ?? ''}
              onChange={(e) =>
                setPayload((prev) => ({
                  ...prev,
                  body: [
                    {
                      ...payload.body[0],
                      whenDue: e.target.value,
                    },
                  ],
                }))
              }
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
            <FormControlLabel
              control={
                <AthenaSwitch
                  sx={{ ml: 1 }}
                  checked={payload.body[0]?.requiresWorkflow}
                  slotProps={{ input: { 'aria-label': 'controlled' } }}
                  onChange={(e, checked) =>
                    setPayload((prev) => ({
                      ...prev,
                      body: [
                        {
                          ...payload.body[0],
                          requiresWorkflow: checked,
                        },
                      ],
                    }))
                  }
                />
              }
              label="Requires Workflow?"
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            mt: 2,
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {error && (
              <>
                <Info sx={{ color: 'red' }} />
                <Typography sx={{ color: 'red' }}>{error}</Typography>
              </>
            )}
          </Box>
          <Button
            disabled={
              isLoading ||
              !isDeliverableNumberAvailable ||
              !payload.body[0]?.deliverableNumber ||
              !payload.body[0]?.deliverableTypeId ||
              !payload.body[0]?.source
            }
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
            variant="contained"
            onClick={async () => {
              try {
                const data = await addType({
                  id: payload.id,
                  handlersProgramDeliverableTypesRequest: {
                    deliverableTypes: payload.body,
                  },
                }).unwrap();

                data &&
                  setProgramDelType((prev) => [
                    ...prev,
                    ...data.map((delTypes) => delTypes),
                  ]);
                setError('');
                handleClose();
              } catch {
                setError('Error in adding deliverable type, Please Try Again.');
              }
            }}
          >
            {isLoading ? 'Adding...' : 'Add'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddTypeModal;
